export const COUNTRIES = [
  "Belgium", "Netherlands", "Germany", "France", "Spain", "Italy",
  "United Kingdom", "Greece", "Ireland", "Austria", "Switzerland",
  "Sweden", "Denmark", "Norway", "Poland", "Turkey", "Portugal",
  "UAE", "Egypt", "Morocco", "Tunisia",
];

export const generateLegId = () => `LEG-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
export const generateRoomId = () => `ROOM-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
export const generateCabinId = () => `CABIN-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
export const generateAdjustmentId = () => `ADJ-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
export const generateNoteId = () => `NOTE-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
export const generateSSRId = () => `SSR-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

// Maps a product type to the service-type label used inside supplier.services.
// Keep in sync with the backend service catalog.
export const SERVICE_KEY_BY_PRODUCT = {
  Hotel: "Hotels",
  Transfer: "Transfers",
  Package: "External Package Holiday",
  Incoming: "Incoming",
  CarRental: "Car Rental",
  Insurance: "Insurance",
  Visa: "Visa",
  Cruise: "Other Services",
  Flight: "Flight Service",
};

export const findSupplierService = (supplier, productType) => {
  if (!supplier || !Array.isArray(supplier.services)) return null;
  const key = SERVICE_KEY_BY_PRODUCT[productType] || productType;
  return supplier.services.find((sv) => (sv.serviceType || sv.key) === key) || null;
};

// Resolves the effective commercial role (MERCHANT | AGENT) for a supplier on
// a given product type. Resolution order:
//   1. service.commercialRole when it is MERCHANT or AGENT
//   2. service.defaultCommercialRole when service.commercialRole is BOTH
//   3. supplier-level commercialRole
//   4. "MERCHANT"
// `BOTH` indicates the user may pick at booking time; callers can use
// `isCommercialRoleFlexible` to decide whether to render a dropdown.
export const resolveSupplierCommercialRole = (supplier, productType) => {
  if (!supplier) return "MERCHANT";
  const supplierFallback = supplier.commercialRole || "MERCHANT";
  const svc = findSupplierService(supplier, productType);
  if (!svc) return supplierFallback;
  const role = svc.commercialRole;
  if (role === "MERCHANT" || role === "AGENT") return role;
  if (role === "BOTH") return svc.defaultCommercialRole || supplierFallback;
  return supplierFallback;
};

export const isCommercialRoleFlexible = (supplier, productType) => {
  const svc = findSupplierService(supplier, productType);
  return svc?.commercialRole === "BOTH";
};

// Last names are stored and displayed in upper case throughout the booking flow.
// Use these helpers so legacy/mixed-case data still renders in CAPS.
export const formatLastName = (s) => (s == null ? "" : String(s)).toUpperCase();
export const formatPersonName = (firstName, lastName, fallback = "") =>
  [firstName, formatLastName(lastName)].filter(Boolean).join(" ") || fallback;

export const calcAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

export const classifyTraveler = (dob) => {
  const age = calcAge(dob);
  if (age === null) return "—";
  if (age < 2) return "INF";
  if (age < 12) return "CHD";
  return "ADT";
};

export const formatCurrency = (val) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" }).format(val || 0);

export const formatCurrentDate = () => {
  const now = new Date();
  return now.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
};

export const calcNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const d1 = new Date(checkIn);
  const d2 = new Date(checkOut);
  const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};


export const newRoom = () => ({
  id: generateRoomId(),
  numberOfRooms: 1, numberOfAdults: 2, numberOfChildren: 0,
  roomType: "", roomName: "", roomCharacteristics: "", boardType: "",
});


export const newSSR = () => ({
  id: generateSSRId(),
  category: "",
  ssrCode: "",
  description: "",
  maxWeight: "",
  maxSize: "",
  maxPieces: "",
  flightProduct: "",
  flightSegment: "",
  travelerIndex: null,
  direction: "",
  salesPrice: 0,
  markup: 0,
  totalPrice: 0,
  notes: "",
  status: "Pending",
});

export const newTransfer = (direction) => ({
  id: generateProductId(),
  productType: "Transfer",
  supplier: "",
  reference: "",
  status: "Pending",
  transferContext: "",
  direction: direction, // "Outbound" or "Inbound" - stored but not displayed
  
  // Transfer Info
  transferType: "",
  vehicleType: "",
  
  // Route
  pickupLocation: "",
  dropoffLocation: "",
  
  // Schedule (field names differ by direction)
  pickupDate: "",
  pickupTime: "",
  arrivalDate: "",
  arrivalTime: "",
  
  // Passenger Info
  numberOfAdults: 2,
  numberOfChildren: 0,
  leadTravelerName: "",
  
  // Flight Details
  flightAirlineCode: "",
  flightNumber: "",
  flightDate: "",
  flightTime: "",
  
  // Supplier / Operational Info
  pickupInstructions: "",
  cantFindInstructions: "",
  travelDisruptionInfo: "",
  contactInfo: "",
  emergencyAssistance: "",
});


export const STEPS = [
  { id: 1, label: "Booking Details", key: "details" },
  { id: 2, label: "Products",        key: "products" },
  { id: 3, label: "Travelers",       key: "travelers" },
  { id: 4, label: "Assignments",     key: "assignments" },
  { id: 5, label: "SSRs",            key: "ssrs" },
  { id: 6, label: "Pricing",         key: "pricing" },
  { id: 7, label: "Summary",         key: "summary" },
];

export const BOOKING_TYPES = [
  { key: "Offline", label: "Offline Booking",  desc: "Manual booking via phone, email, or external channel" },
  { key: "Online",  label: "Online Booking",   desc: "Booking created through website or online portal" },
  { key: "API",     label: "API Booking",      desc: "Booking created through API integration" },
];

export const SALE_TYPES = [
  { key: "PACKAGE_TRAVEL",     label: "Package Travel",     desc: "Bundled products — margin-based VAT applies" },
  { key: "SEPARATE_SERVICES",  label: "Separate Services",  desc: "Individual products — standard VAT per product" },
];

export const PRODUCT_TYPES = [
  { key: "Flight",    label: "Flight",                desc: "Outbound, return, or multi-leg segments" },
  { key: "Hotel",     label: "Hotel / Accommodation", desc: "Hotel stay with check-in/out dates" },
  { key: "Transfer",  label: "Transfer",              desc: "Ground transport between locations" },
  { key: "Insurance", label: "Travel Insurance",      desc: "Travel and cancellation insurance" },
  { key: "Package",   label: "Package Holiday",       desc: "Pre-packaged offline holiday product" },
  { key: "CarRental", label: "Car Rental",            desc: "Rental vehicle for the travel period" },
  { key: "Visa",      label: "Visa Service",          desc: "Visa application and processing" },
  { key: "Cruise",    label: "Cruise",                desc: "Cruise line booking with cabin allocation" },
];

export const PRODUCT_STATUSES = ["Pending", "Option", "Confirmed", "Cancelled"];
export const GENDERS = ["Male", "Female", "Other"];

export const FLIGHT_TYPES = [
  { key: "OW", label: "One Way" },
  { key: "RT", label: "Round Trip" },
  { key: "Multi", label: "Multi-City" },
];

export const FLIGHT_PRICING_MODES = [
  { key: "FULL", label: "Full Flight" },
  { key: "SPLIT", label: "Split Outbound / Inbound" },
];

export const CABIN_CLASSES = ["Economy", "Premium Economy", "Business", "First"];

export const FLIGHT_STATUSES = ["Pending", "Confirmed", "Ticketed", "Cancelled"];

export const TRANSFER_DIRECTIONS = [
  { key: "Outbound", label: "Outbound" },
  { key: "Inbound", label: "Inbound" },
];

export const TRANSFER_TYPES = [
  { key: "Shared", label: "Shared" },
  { key: "Private", label: "Private" },
];

export const TRANSFER_CONTEXT = [
  { key: "Standalone", label: "Standalone Transfer" },
  { key: "WithinBooking", label: "Transfer within Travel Booking" },
];

export const VEHICLE_TYPES = [
  { key: "Car", label: "Car" },
  { key: "Minibus", label: "Minibus" },
  { key: "Bus", label: "Bus" },
  { key: "Van", label: "Van" },
  { key: "SUV", label: "SUV" },
  { key: "Shuttle", label: "Shuttle" },
  { key: "ExecutiveVehicle", label: "Executive Vehicle" },
  { key: "Other", label: "Other" },
];

export const TRANSFER_PRICING_MODES = [
  { key: "FULL", label: "Full Transfer" },
  { key: "SPLIT", label: "Split Outbound / Inbound" },
];

export const INSURANCE_TYPES = ["Travel Insurance", "Cancellation Insurance", "Travel + Cancellation"];

export const INSURANCE_CONTEXT = [
  { key: "Standalone", label: "Standalone Insurance" },
  { key: "WithinBooking", label: "Insurance within Travel Booking" },
];

export const CAR_CATEGORIES = ["Economy", "Compact", "SUV", "Luxury"];

export const CAR_RENTAL_CONTEXT = [
  { key: "Standalone", label: "Standalone Car Rental" },
  { key: "WithinBooking", label: "Car Rental within Travel Booking" },
];

export const VISA_TYPES = ["Tourist", "Business", "Transit"];
export const VISA_ENTRY_TYPES = ["Single", "Multiple"];
export const VISA_DECISION_STATUSES = ["Pending", "Approved", "Rejected"];

export const VISA_CONTEXT = [
  { key: "Standalone", label: "Standalone Visa" },
  { key: "WithinBooking", label: "Visa within Travel Booking" },
];

export const CABIN_TYPES_CRUISE = ["Inside", "Oceanview", "Balcony", "Suite"];
export const BOARD_TYPES_CRUISE = ["Full Board", "All Inclusive"];

export const CRUISE_CONTEXT = [
  { key: "Standalone", label: "Standalone Cruise" },
  { key: "WithinBooking", label: "Cruise within Travel Booking" },
];

export const PACKAGE_TYPES = [
  { key: "External", label: "External Package (Tour Operator)" },
  { key: "Custom", label: "Custom Package (Own Composition)" },
];

export const HOTEL_BOARD_TYPES = ["Room Only", "Bed & Breakfast", "Half Board", "Full Board", "All Inclusive"];

/**
 * Comprehensive board code list — grouped by category.
 * Each entry: { code, name, included, group }
 *   code     — official board code
 *   name     — official display name
 *   included — what meals / services are included
 *   group    — category for grouped dropdown
 */
export const BOARD_CODES_FULL = [
  // 1. Room Only / No Meals
  { code: "RO",  name: "Room Only",            included: "No meals included",                                 group: "Room Only / No Meals" },
  { code: "EP",  name: "European Plan",         included: "No meals included",                                 group: "Room Only / No Meals" },
  { code: "BO",  name: "Bed Only",              included: "No meals included",                                 group: "Room Only / No Meals" },
  { code: "AO",  name: "Accommodation Only",    included: "No meals included",                                 group: "Room Only / No Meals" },
  { code: "NO",  name: "No Meals",              included: "No meals included",                                 group: "Room Only / No Meals" },
  // 2. Breakfast Only
  { code: "BB",  name: "Bed & Breakfast",        included: "Breakfast",                                        group: "Breakfast Only" },
  { code: "CP",  name: "Continental Plan",       included: "Continental breakfast",                             group: "Breakfast Only" },
  { code: "ABF", name: "American Breakfast",     included: "Full American breakfast",                           group: "Breakfast Only" },
  { code: "CBF", name: "Continental Breakfast",  included: "Continental breakfast (light)",                     group: "Breakfast Only" },
  { code: "EBF", name: "English Breakfast",      included: "Full English breakfast",                            group: "Breakfast Only" },
  { code: "SBF", name: "Scandinavian Breakfast", included: "Scandinavian buffet breakfast",                     group: "Breakfast Only" },
  // 3. Half Board
  { code: "HB",  name: "Half Board",             included: "Breakfast + Dinner",                               group: "Half Board" },
  { code: "HB+", name: "Half Board Plus",        included: "Breakfast + Dinner + selected drinks",             group: "Half Board" },
  { code: "HBD", name: "Half Board Dinner",      included: "Breakfast + Dinner",                               group: "Half Board" },
  { code: "HBL", name: "Half Board Lunch",       included: "Breakfast + Lunch",                                group: "Half Board" },
  { code: "MAP", name: "Modified American Plan", included: "Breakfast + Dinner",                               group: "Half Board" },
  // 4. Full Board
  { code: "FB",  name: "Full Board",             included: "Breakfast + Lunch + Dinner",                       group: "Full Board" },
  { code: "FB+", name: "Full Board Plus",        included: "Breakfast + Lunch + Dinner + selected drinks",     group: "Full Board" },
  { code: "AP",  name: "American Plan",          included: "Breakfast + Lunch + Dinner",                       group: "Full Board" },
  { code: "FBD", name: "Full Board Deluxe",      included: "Breakfast + Lunch + Dinner + premium drinks",      group: "Full Board" },
  { code: "FBP", name: "Full Board Premium",     included: "Breakfast + Lunch + Dinner + premium beverages",   group: "Full Board" },
  // 5. All Inclusive
  { code: "AI",  name: "All Inclusive",           included: "All meals + local drinks + snacks",                group: "All Inclusive" },
  { code: "AIP", name: "All Inclusive Premium",   included: "All meals + premium drinks + snacks + activities", group: "All Inclusive" },
  { code: "AI+", name: "All Inclusive Plus",      included: "All meals + selected imported drinks + snacks",   group: "All Inclusive" },
  { code: "UAI", name: "Ultra All Inclusive",     included: "All meals + all drinks + à la carte + activities",group: "All Inclusive" },
  { code: "UAI+",name: "Ultra All Inclusive Plus",included: "All meals + all drinks + à la carte + spa + activities", group: "All Inclusive" },
  { code: "HCAI",name: "High Class All Inclusive",included: "All meals + premium drinks + à la carte + VIP services", group: "All Inclusive" },
  { code: "SAI", name: "Soft All Inclusive",      included: "All meals + limited local drinks",                group: "All Inclusive" },
  { code: "MAI", name: "Mega All Inclusive",      included: "All meals + all drinks + all activities + spa",   group: "All Inclusive" },
  { code: "EAI", name: "Extreme All Inclusive",   included: "All meals + all drinks + all activities + excursions", group: "All Inclusive" },
  { code: "PAI", name: "Premium All Inclusive",   included: "All meals + premium drinks + à la carte + minibar", group: "All Inclusive" },
  // 6. Special / Resort Variants
  { code: "DAI",       name: "Deluxe All Inclusive",    included: "All meals + premium drinks + room service + spa", group: "Special / Resort" },
  { code: "LAI",       name: "Luxury All Inclusive",    included: "All meals + top-shelf drinks + butler service",   group: "Special / Resort" },
  { code: "FAI",       name: "Family All Inclusive",    included: "All meals + drinks + kids club + family activities", group: "Special / Resort" },
  { code: "MINI AI",   name: "Mini All Inclusive",      included: "All meals + limited drinks (set hours)",          group: "Special / Resort" },
  { code: "LIGHT AI",  name: "Light All Inclusive",     included: "Breakfast + Dinner + local drinks",               group: "Special / Resort" },
  { code: "RESORT AI", name: "Resort All Inclusive",    included: "All meals + drinks + resort facilities",          group: "Special / Resort" },
  { code: "PREMIUM AI",name: "Premium All Inclusive",   included: "All meals + premium drinks + à la carte dining",  group: "Special / Resort" },
  { code: "SELECT AI", name: "Select All Inclusive",    included: "All meals + curated drink selection + snacks",    group: "Special / Resort" },
  // 7. Dinner / Lunch Specific
  { code: "DBB",    name: "Dinner Bed & Breakfast", included: "Breakfast + Dinner",          group: "Dinner / Lunch" },
  { code: "LBB",    name: "Lunch Bed & Breakfast",  included: "Breakfast + Lunch",           group: "Dinner / Lunch" },
  { code: "DINNER", name: "Dinner Included",        included: "Dinner only",                 group: "Dinner / Lunch" },
  { code: "LUNCH",  name: "Lunch Included",         included: "Lunch only",                  group: "Dinner / Lunch" },
  { code: "DP",     name: "Dinner Plan",            included: "Dinner only",                 group: "Dinner / Lunch" },
  { code: "LP",     name: "Lunch Plan",             included: "Lunch only",                  group: "Dinner / Lunch" },
  // 8. Beverage / Plus Variants
  { code: "AI PREMIUM",  name: "Premium AI Beverages",  included: "All meals + premium branded drinks",      group: "Beverage / Plus" },
  { code: "AI LOCAL",    name: "Local AI Drinks",       included: "All meals + local drinks only",            group: "Beverage / Plus" },
  { code: "AI IMPORTED", name: "Imported AI Drinks",    included: "All meals + imported branded drinks",      group: "Beverage / Plus" },
  { code: "AI 24H",      name: "24 Hour All Inclusive", included: "All meals + all drinks 24 hours + snacks",group: "Beverage / Plus" },
  // 9. Cruise / Special Travel
  { code: "SC",    name: "Self Catering",                included: "No meals — self-catering kitchen",         group: "Cruise / Special" },
  { code: "OB",    name: "Only Bed",                     included: "No meals included",                        group: "Cruise / Special" },
  { code: "VP",    name: "Voll Pension",                  included: "Breakfast + Lunch + Dinner",               group: "Cruise / Special" },
  { code: "HP",    name: "Halbpension",                   included: "Breakfast + Dinner",                       group: "Cruise / Special" },
  { code: "FI",    name: "Full Inclusive",                 included: "All meals + all drinks + entertainment",   group: "Cruise / Special" },
  { code: "FAP",   name: "Full American Plan",            included: "Breakfast + Lunch + Dinner",               group: "Cruise / Special" },
  { code: "MAPAI", name: "MAP + AI Combination",          included: "Breakfast + Dinner + all-inclusive drinks", group: "Cruise / Special" },
  { code: "SAP",   name: "Special American Plan",         included: "Breakfast + Lunch + Dinner (premium)",     group: "Cruise / Special" },
  // 10. Rare / Legacy / Regional
  { code: "BAI",            name: "Budget All Inclusive",  included: "All meals + basic local drinks",           group: "Rare / Legacy" },
  { code: "RAI",            name: "Resort All Inclusive",  included: "All meals + drinks + resort facilities",   group: "Rare / Legacy" },
  { code: "VAI",            name: "VIP All Inclusive",     included: "All meals + premium drinks + VIP services",group: "Rare / Legacy" },
  { code: "PENSION",        name: "Pension Included",      included: "Breakfast + Dinner (pension style)",       group: "Rare / Legacy" },
  { code: "SELF CATERING",  name: "Self Catering",         included: "No meals — self-catering facilities",      group: "Rare / Legacy" },
  { code: "CHALET ONLY",    name: "Chalet Only",           included: "No meals — chalet accommodation only",     group: "Rare / Legacy" },
  { code: "VILLA ONLY",     name: "Villa Only",            included: "No meals — villa accommodation only",      group: "Rare / Legacy" },
  { code: "ROOM + BREAKFAST",name: "Room + Breakfast",     included: "Breakfast",                                group: "Rare / Legacy" },
];

/** Quick lookup: code → name */
export const BOARD_CODE_TO_NAME = Object.fromEntries(BOARD_CODES_FULL.map(b => [b.code, b.name]));
/** Quick lookup: name → code */
export const BOARD_NAME_TO_CODE = Object.fromEntries(BOARD_CODES_FULL.map(b => [b.name, b.code]));
/** All group names in order */
export const BOARD_GROUPS = [...new Set(BOARD_CODES_FULL.map(b => b.group))];

/**
 * Comprehensive room types — ALL room descriptors in one searchable list.
 * Each entry: { value, group }
 */
export const ROOM_TYPES_FULL = [
  // Standard Rooms
  { value: "Standard Room",   group: "Standard Rooms" },
  { value: "Superior Room",   group: "Standard Rooms" },
  { value: "Deluxe Room",     group: "Standard Rooms" },
  { value: "Premium Room",    group: "Standard Rooms" },
  { value: "Executive Room",  group: "Standard Rooms" },
  { value: "Club Room",       group: "Standard Rooms" },
  { value: "Economy Room",    group: "Standard Rooms" },
  { value: "Promo Room",      group: "Standard Rooms" },
  { value: "Budget Room",     group: "Standard Rooms" },
  { value: "Classic Room",    group: "Standard Rooms" },
  { value: "Comfort Room",    group: "Standard Rooms" },
  { value: "Luxury Room",     group: "Standard Rooms" },
  { value: "Prestige Room",   group: "Standard Rooms" },
  { value: "Signature Room",  group: "Standard Rooms" },
  // Bed Configuration
  { value: "Single Room",     group: "Bed Configuration" },
  { value: "Double Room",     group: "Bed Configuration" },
  { value: "Twin Room",       group: "Bed Configuration" },
  { value: "Triple Room",     group: "Bed Configuration" },
  { value: "Quad Room",       group: "Bed Configuration" },
  { value: "Family Room",     group: "Bed Configuration" },
  // Suites
  { value: "Suite",              group: "Suites" },
  { value: "Family Suite",      group: "Suites" },
  { value: "Junior Suite",      group: "Suites" },
  { value: "Executive Suite",   group: "Suites" },
  { value: "Deluxe Suite",      group: "Suites" },
  { value: "Presidential Suite",group: "Suites" },
  { value: "Royal Suite",       group: "Suites" },
  { value: "Penthouse Suite",   group: "Suites" },
  { value: "Honeymoon Suite",   group: "Suites" },
  { value: "Duplex Suite",      group: "Suites" },
  { value: "Corner Suite",      group: "Suites" },
  { value: "Spa Suite",         group: "Suites" },
  { value: "Ocean Suite",       group: "Suites" },
  { value: "Panorama Suite",    group: "Suites" },
  // Connected / Layout
  { value: "Duplex",              group: "Connected / Layout" },
  { value: "Connecting Room",     group: "Connected / Layout" },
  { value: "Interconnecting Room",group: "Connected / Layout" },
  { value: "Adjoining Room",      group: "Connected / Layout" },
  // Villas & Alternative
  { value: "Studio",          group: "Villas & Alternative" },
  { value: "Apartment",       group: "Villas & Alternative" },
  { value: "Residence",       group: "Villas & Alternative" },
  { value: "Condo",           group: "Villas & Alternative" },
  { value: "Villa",           group: "Villas & Alternative" },
  { value: "Beach Villa",     group: "Villas & Alternative" },
  { value: "Pool Villa",      group: "Villas & Alternative" },
  { value: "Water Villa",     group: "Villas & Alternative" },
  { value: "Overwater Villa", group: "Villas & Alternative" },
  { value: "Garden Villa",    group: "Villas & Alternative" },
  { value: "Bungalow",        group: "Villas & Alternative" },
  { value: "Chalet",          group: "Villas & Alternative" },
  { value: "Cottage",         group: "Villas & Alternative" },
  { value: "Cabin",           group: "Villas & Alternative" },
  // Special Room Types
  { value: "Swim Up Room",      group: "Special Rooms" },
  { value: "Pool Access Room",  group: "Special Rooms" },
  { value: "Accessible Room",   group: "Special Rooms" },
  { value: "Smoking Room",      group: "Special Rooms" },
  { value: "Non Smoking Room",  group: "Special Rooms" },
  { value: "Capsule Room",      group: "Special Rooms" },
  { value: "Dormitory Room",    group: "Special Rooms" },
  { value: "Shared Room",       group: "Special Rooms" },
  { value: "Transit Room",      group: "Special Rooms" },
  { value: "Day Use Room",      group: "Special Rooms" },
  // Sea & Ocean View
  { value: "Sea View",          group: "Sea & Ocean View" },
  { value: "Partial Sea View",  group: "Sea & Ocean View" },
  { value: "Side Sea View",     group: "Sea & Ocean View" },
  { value: "Front Sea View",    group: "Sea & Ocean View" },
  { value: "Direct Sea View",   group: "Sea & Ocean View" },
  { value: "Ocean View",        group: "Sea & Ocean View" },
  { value: "Oceanfront",        group: "Sea & Ocean View" },
  { value: "Ocean Front View",  group: "Sea & Ocean View" },
  // Beach & Lagoon View
  { value: "Beach View",        group: "Beach & Water View" },
  { value: "Beachfront",        group: "Beach & Water View" },
  { value: "Lagoon View",       group: "Beach & Water View" },
  { value: "Bay View",          group: "Beach & Water View" },
  { value: "Harbor View",       group: "Beach & Water View" },
  { value: "Marina View",       group: "Beach & Water View" },
  { value: "Gulf View",         group: "Beach & Water View" },
  { value: "Waterfront View",   group: "Beach & Water View" },
  { value: "Water View",        group: "Beach & Water View" },
  // Pool View
  { value: "Pool View",         group: "Pool View" },
  { value: "Poolside View",     group: "Pool View" },
  { value: "Infinity Pool View",group: "Pool View" },
  { value: "Swim Up View",      group: "Pool View" },
  // Garden & Nature View
  { value: "Garden View",       group: "Garden & Nature View" },
  { value: "Nature View",       group: "Garden & Nature View" },
  { value: "Forest View",       group: "Garden & Nature View" },
  { value: "Jungle View",       group: "Garden & Nature View" },
  { value: "Park View",         group: "Garden & Nature View" },
  { value: "River View",        group: "Garden & Nature View" },
  { value: "Lake View",         group: "Garden & Nature View" },
  { value: "Countryside View",  group: "Garden & Nature View" },
  // Mountain & Landscape View
  { value: "Mountain View",     group: "Mountain & Landscape View" },
  { value: "Snow View",         group: "Mountain & Landscape View" },
  { value: "Golf View",         group: "Mountain & Landscape View" },
  { value: "Resort View",       group: "Mountain & Landscape View" },
  { value: "Courtyard View",    group: "Mountain & Landscape View" },
  { value: "Atrium View",       group: "Mountain & Landscape View" },
  { value: "Landmark View",     group: "Mountain & Landscape View" },
  // City & Urban View
  { value: "City View",         group: "City & Urban View" },
  { value: "Skyline View",      group: "City & Urban View" },
  { value: "Street View",       group: "City & Urban View" },
  { value: "Urban View",        group: "City & Urban View" },
  { value: "Inland View",       group: "City & Urban View" },
  { value: "Land View",         group: "City & Urban View" },
  // Panoramic & Special View
  { value: "Panorama View",     group: "Panoramic & Special View" },
  { value: "Panoramic View",    group: "Panoramic & Special View" },
  { value: "Sunset View",       group: "Panoramic & Special View" },
  { value: "Sunrise View",      group: "Panoramic & Special View" },
  { value: "Tennis View",       group: "Panoramic & Special View" },
  // Building & Wing
  { value: "Main Building",     group: "Building & Wing" },
  { value: "Annex Building",    group: "Building & Wing" },
  { value: "Annex Side",        group: "Building & Wing" },
  { value: "Annex Block",       group: "Building & Wing" },
  { value: "Secondary Building",group: "Building & Wing" },
  { value: "Residence Building",group: "Building & Wing" },
  { value: "Tower Section",     group: "Building & Wing" },
  { value: "Tower Building",    group: "Building & Wing" },
  { value: "Tower Side",        group: "Building & Wing" },
  { value: "Main Wing",         group: "Building & Wing" },
  { value: "East Wing",         group: "Building & Wing" },
  { value: "West Wing",         group: "Building & Wing" },
  { value: "North Wing",        group: "Building & Wing" },
  { value: "South Wing",        group: "Building & Wing" },
  { value: "Private Wing",      group: "Building & Wing" },
  { value: "Club Wing",         group: "Building & Wing" },
  { value: "Garden Wing",       group: "Building & Wing" },
  { value: "Beach Wing",        group: "Building & Wing" },
  { value: "Pool Wing",         group: "Building & Wing" },
  { value: "Family Wing",       group: "Building & Wing" },
  { value: "Executive Wing",    group: "Building & Wing" },
  { value: "VIP Wing",          group: "Building & Wing" },
  // Floor Level
  { value: "Ground Floor",      group: "Floor Level" },
  { value: "Basement Level",    group: "Floor Level" },
  { value: "Low Floor",         group: "Floor Level" },
  { value: "Mid Floor",         group: "Floor Level" },
  { value: "High Floor",        group: "Floor Level" },
  { value: "Upper Floor",       group: "Floor Level" },
  { value: "Top Floor",         group: "Floor Level" },
  { value: "Penthouse Level",   group: "Floor Level" },
  { value: "Rooftop Level",     group: "Floor Level" },
  { value: "Mezzanine Level",   group: "Floor Level" },
  // Room Position
  { value: "Corner Room",          group: "Room Position" },
  { value: "Corner Position",      group: "Room Position" },
  { value: "Central Position",     group: "Room Position" },
  { value: "End Of Corridor",      group: "Room Position" },
  { value: "Near Elevator",        group: "Room Position" },
  { value: "Away From Elevator",   group: "Room Position" },
  { value: "Near Reception",       group: "Room Position" },
  { value: "Near Lobby",           group: "Room Position" },
  { value: "Near Pool",            group: "Room Position" },
  { value: "Near Beach",           group: "Room Position" },
  { value: "Near Restaurant",      group: "Room Position" },
  { value: "Near Spa",             group: "Room Position" },
  { value: "Near Aqua Park",       group: "Room Position" },
  { value: "Near Kids Club",       group: "Room Position" },
  { value: "Near Parking",         group: "Room Position" },
  { value: "Near Golf Course",     group: "Room Position" },
  { value: "Near Tennis Court",    group: "Room Position" },
  // Frontage & Side
  { value: "Seafront",         group: "Frontage & Side" },
  { value: "Sea Facing",       group: "Frontage & Side" },
  { value: "Sea Side",         group: "Frontage & Side" },
  { value: "Seaside",          group: "Frontage & Side" },
  { value: "Sea Front",        group: "Frontage & Side" },
  { value: "Front Sea Side",   group: "Frontage & Side" },
  { value: "Side Sea Side",    group: "Frontage & Side" },
  { value: "Partial Sea Side", group: "Frontage & Side" },
  { value: "Direct Sea Side",  group: "Frontage & Side" },
  { value: "Ocean Side",       group: "Frontage & Side" },
  { value: "Ocean Front",      group: "Frontage & Side" },
  { value: "Beach Side",       group: "Frontage & Side" },
  { value: "Waterfront Side",  group: "Frontage & Side" },
  { value: "Water Edge",       group: "Frontage & Side" },
  { value: "Lagoon Side",      group: "Frontage & Side" },
  { value: "Lagoon Front",     group: "Frontage & Side" },
  { value: "Bay Side",         group: "Frontage & Side" },
  { value: "Harbor Side",      group: "Frontage & Side" },
  { value: "Marina Side",      group: "Frontage & Side" },
  { value: "Marina Front",     group: "Frontage & Side" },
  { value: "Gulf Side",        group: "Frontage & Side" },
  { value: "Coast Side",       group: "Frontage & Side" },
  { value: "Coastal Side",     group: "Frontage & Side" },
  { value: "Waterfront",       group: "Frontage & Side" },
  { value: "Poolfront",        group: "Frontage & Side" },
  { value: "Pool Front",       group: "Frontage & Side" },
  { value: "Pool Side",        group: "Frontage & Side" },
  { value: "Garden Side",      group: "Frontage & Side" },
  { value: "Garden Front",     group: "Frontage & Side" },
  { value: "Gardenfront",      group: "Frontage & Side" },
  { value: "Courtyard Side",   group: "Frontage & Side" },
  { value: "Courtyard Front",  group: "Frontage & Side" },
  { value: "Inner Courtyard Side", group: "Frontage & Side" },
  { value: "Park Side",        group: "Frontage & Side" },
  { value: "Golf Side",        group: "Frontage & Side" },
  { value: "Golf Front",       group: "Frontage & Side" },
  { value: "Resort Side",      group: "Frontage & Side" },
  { value: "Resort Front",     group: "Frontage & Side" },
  { value: "Lakefront",        group: "Frontage & Side" },
  { value: "Lake Side",        group: "Frontage & Side" },
  { value: "Riverfront",       group: "Frontage & Side" },
  { value: "River Side",       group: "Frontage & Side" },
  { value: "Mountain Side",    group: "Frontage & Side" },
  { value: "Mountain Front",   group: "Frontage & Side" },
  { value: "Forest Side",      group: "Frontage & Side" },
  { value: "Jungle Side",      group: "Frontage & Side" },
  { value: "Nature Side",      group: "Frontage & Side" },
  { value: "Cliff Side",       group: "Frontage & Side" },
  { value: "Land Side",        group: "Frontage & Side" },
  { value: "Inland Side",      group: "Frontage & Side" },
  { value: "Country Side",     group: "Frontage & Side" },
  { value: "Countryside Side", group: "Frontage & Side" },
  { value: "Street Side",      group: "Frontage & Side" },
  { value: "City Side",        group: "Frontage & Side" },
  { value: "Village Side",     group: "Frontage & Side" },
  { value: "Residential Side", group: "Frontage & Side" },
  { value: "Main Building Side",group: "Frontage & Side" },
  { value: "Quiet Side",       group: "Frontage & Side" },
  { value: "Sunset Side",      group: "Frontage & Side" },
  { value: "Sunrise Side",     group: "Frontage & Side" },
  { value: "Panorama Side",    group: "Frontage & Side" },
  { value: "Panoramic Position",group: "Frontage & Side" },
  { value: "Island Side",      group: "Frontage & Side" },
  { value: "Private Island Side",group: "Frontage & Side" },
  { value: "Yacht Marina Side",  group: "Frontage & Side" },
  { value: "Overwater Position",  group: "Frontage & Side" },
  { value: "Water Villa Position",group: "Frontage & Side" },
  // Direct Access
  { value: "Direct Beach Access",  group: "Direct Access" },
  { value: "Direct Pool Access",   group: "Direct Access" },
  { value: "Direct Sea Access",    group: "Direct Access" },
  { value: "Lagoon Access",        group: "Direct Access" },
  { value: "Marina Access",        group: "Direct Access" },
  { value: "Yacht Access",         group: "Direct Access" },
  // Outdoor Features
  { value: "Balcony",          group: "Outdoor Features" },
  { value: "Terrace",          group: "Outdoor Features" },
  { value: "Patio",            group: "Outdoor Features" },
  { value: "Veranda",          group: "Outdoor Features" },
  { value: "Private Garden",   group: "Outdoor Features" },
  { value: "Sun Deck",         group: "Outdoor Features" },
  // Water Features
  { value: "Jacuzzi",          group: "Water Features" },
  { value: "Whirlpool",        group: "Water Features" },
  { value: "Hot Tub",          group: "Water Features" },
  { value: "Private Pool",     group: "Water Features" },
  { value: "Shared Pool",      group: "Water Features" },
  { value: "Infinity Pool",    group: "Water Features" },
  { value: "Swim Up",          group: "Water Features" },
  { value: "Pool Access",      group: "Water Features" },
  // Wellness & Spa
  { value: "Spa Access",       group: "Wellness & Spa" },
  { value: "Sauna",            group: "Wellness & Spa" },
  { value: "Steam Room",       group: "Wellness & Spa" },
  { value: "Massage Area",     group: "Wellness & Spa" },
  { value: "Wellness Access",  group: "Wellness & Spa" },
  // Kitchen & Dining
  { value: "Kitchenette",      group: "Kitchen & Dining" },
  { value: "Full Kitchen",     group: "Kitchen & Dining" },
  { value: "Dining Area",      group: "Kitchen & Dining" },
  { value: "Coffee Machine",   group: "Kitchen & Dining" },
  { value: "Espresso Machine", group: "Kitchen & Dining" },
  { value: "Mini Bar",         group: "Kitchen & Dining" },
  { value: "Private Bar",      group: "Kitchen & Dining" },
  // Services & Lounge
  { value: "Lounge Access",      group: "Services & Lounge" },
  { value: "Club Access",        group: "Services & Lounge" },
  { value: "Executive Lounge",   group: "Services & Lounge" },
  { value: "Exclusive Lounge",   group: "Services & Lounge" },
  { value: "Butler Service",     group: "Services & Lounge" },
  { value: "Concierge Service",  group: "Services & Lounge" },
  { value: "VIP Service",        group: "Services & Lounge" },
  { value: "Private Concierge",  group: "Services & Lounge" },
  { value: "Beach Club Access",  group: "Services & Lounge" },
  // Room Amenities
  { value: "Work Desk",         group: "Room Amenities" },
  { value: "Meeting Area",      group: "Room Amenities" },
  { value: "Air Conditioning",  group: "Room Amenities" },
  { value: "Heating",           group: "Room Amenities" },
  { value: "Soundproof",        group: "Room Amenities" },
  { value: "Blackout Curtains", group: "Room Amenities" },
  { value: "Luxury Toiletries", group: "Room Amenities" },
  { value: "Rain Shower",       group: "Room Amenities" },
  { value: "Bathtub",           group: "Room Amenities" },
  { value: "Walk In Shower",    group: "Room Amenities" },
  { value: "Smart TV",          group: "Room Amenities" },
  { value: "Home Cinema",       group: "Room Amenities" },
  { value: "Bluetooth Speaker", group: "Room Amenities" },
  { value: "High Speed WiFi",   group: "Room Amenities" },
  { value: "USB Charging Ports",group: "Room Amenities" },
  { value: "Premium Amenities", group: "Room Amenities" },
  // Bedding & Family
  { value: "Sofa Bed",        group: "Bedding & Family" },
  { value: "Extra Bed",       group: "Bedding & Family" },
  { value: "Rollaway Bed",    group: "Bedding & Family" },
  { value: "Murphy Bed",      group: "Bedding & Family" },
  { value: "Baby Cot",        group: "Bedding & Family" },
  { value: "Bunk Beds",       group: "Bedding & Family" },
  { value: "Child Friendly",  group: "Bedding & Family" },
  // Accessibility
  { value: "Mobility Accessible",   group: "Accessibility" },
  { value: "Hearing Accessible",    group: "Accessibility" },
  { value: "Wheelchair Accessible", group: "Accessibility" },
  { value: "Allergy Friendly",      group: "Accessibility" },
  // Lifestyle & Policy
  { value: "Pet Friendly",    group: "Lifestyle & Policy" },
  { value: "Smoking",         group: "Lifestyle & Policy" },
  { value: "Non Smoking",     group: "Lifestyle & Policy" },
  { value: "Adults Only",     group: "Lifestyle & Policy" },
  { value: "Adults Only Area",group: "Lifestyle & Policy" },
  { value: "Family Area",     group: "Lifestyle & Policy" },
  { value: "Quiet Area",      group: "Lifestyle & Policy" },
  { value: "Private Area",    group: "Lifestyle & Policy" },
  { value: "Exclusive Area",  group: "Lifestyle & Policy" },
  { value: "Secluded Area",   group: "Lifestyle & Policy" },
  // Private Access
  { value: "Private Entrance",  group: "Private Access" },
  { value: "Gated Access",      group: "Private Access" },
  { value: "Private Check In",  group: "Private Access" },
  { value: "Private Beach",     group: "Private Access" },
  // Activity Access
  { value: "Diving Access",       group: "Activity Access" },
  { value: "Snorkeling Access",   group: "Activity Access" },
  { value: "Surf Access",         group: "Activity Access" },
  { value: "Windsurf Access",     group: "Activity Access" },
  { value: "Sailing Access",      group: "Activity Access" },
  { value: "Kayak Access",        group: "Activity Access" },
  { value: "Paddle Board Access", group: "Activity Access" },
  { value: "Golf Access",         group: "Activity Access" },
  { value: "Golf Package",        group: "Activity Access" },
  { value: "Tennis Access",       group: "Activity Access" },
  { value: "Gym Access",          group: "Activity Access" },
  { value: "Fitness Access",      group: "Activity Access" },
  { value: "Personal Training Access", group: "Activity Access" },
  { value: "Ski Access",          group: "Activity Access" },
  { value: "Ski In Ski Out",      group: "Activity Access" },
  { value: "Mountain Access",     group: "Activity Access" },
  { value: "Aqua Park Access",    group: "Activity Access" },
  { value: "Kids Club Access",    group: "Activity Access" },
  { value: "Playground Access",   group: "Activity Access" },
  // Premium & Booking Designations
  { value: "Luxury Collection",     group: "Premium & Designations" },
  { value: "Signature Collection",  group: "Premium & Designations" },
  { value: "Promo Room",            group: "Premium & Designations" },
  { value: "Run Of House (ROH)",    group: "Premium & Designations" },
  { value: "Best Available Room",   group: "Premium & Designations" },
  { value: "Assigned On Arrival",   group: "Premium & Designations" },
  { value: "Mystery Room",         group: "Premium & Designations" },
  { value: "Economy Annex Room",   group: "Premium & Designations" },
  { value: "Main Building Room",   group: "Premium & Designations" },
];

/** Same list used for room characteristics — both dropdowns share the full list */
export const ROOM_CHARS_FULL = ROOM_TYPES_FULL;

/** All room type group names in order */
export const ROOM_TYPE_GROUPS = [...new Set(ROOM_TYPES_FULL.map(r => r.group))];

export const CANCELLATION_TYPES = ["Free Cancellation", "Non-refundable", "Partial Refund"];

export const ADJUSTMENT_TYPES = ["Discount", "Surcharge"];
export const ADJUSTMENT_MODES = ["Percentage", "Fixed"];

export const BOOKING_STATUSES = ["Pending", "Confirmed", "Cancelled"];
export const PAYMENT_STATUSES = ["Unpaid", "Partially Paid", "Paid"];

export const SSR_CATEGORIES = [
  { code: "SEAT", name: "Seat", icon: "seat" },
  { code: "MEAL", name: "Meal", icon: "meal" },
  { code: "HAND_BAGGAGE", name: "Hand Baggage", icon: "baggage" },
  { code: "BAGGAGE", name: "Baggage", icon: "baggage" },
  { code: "ASSISTANCE", name: "Assistance", icon: "assistance" },
  { code: "SPORT_EQUIPMENT", name: "Sport Equipment", icon: "sport" },
  { code: "PET", name: "Pet", icon: "pet" },
  { code: "PERSONAL_ITEM", name: "Personal Item", icon: "personal" },
  { code: "OTHER", name: "Other", icon: "other" },
];

export const SSR_STATUSES = ["Confirmed", "Not Confirmed", "Pending", "Cancelled"];

export const SSR_DIRECTIONS = ["Outbound", "Inbound", "Both"];

export const MOCK_CUSTOMERS = [
  { id: 1,  name: "Sarah Johnson",           email: "sarah.j@email.com",           type: "Private", country: "Belgium",        phone: "+32 470 123 456",   totalBookings: 4,  city: "Brussels",    streetName: "Rue de la Loi",       streetNo: "42",  houseNo: "A",  boxNo: "3"   },
  { id: 2,  name: "Emma Wilson",             email: "emma.w@email.com",            type: "Private", country: "United Kingdom", phone: "+44 7700 900123",   totalBookings: 2,  city: "London",      streetName: "Baker Street",        streetNo: "221", houseNo: "B",  boxNo: ""    },
  { id: 3,  name: "James Brown",             email: "james.b@email.com",           type: "Private", country: "United States",  phone: "+1 202 555 0174",   totalBookings: 7,  city: "Washington",  streetName: "Pennsylvania Ave",    streetNo: "1600",houseNo: "",   boxNo: ""    },
  { id: 4,  name: "Olivia Smith",            email: "olivia.s@email.com",          type: "Private", country: "United Kingdom", phone: "+44 7911 123456",   totalBookings: 3,  city: "Manchester",  streetName: "Deansgate",           streetNo: "78",  houseNo: "",   boxNo: "12"  },
  { id: 5,  name: "Marie Dubois",            email: "marie.d@email.fr",            type: "Private", country: "France",         phone: "+33 6 12 34 56 78", totalBookings: 5,  city: "Paris",       streetName: "Avenue des Champs-Élysées", streetNo: "15", houseNo: "", boxNo: "7"   },
  { id: 11, name: "Horizon Travel Group",    email: "accounts@horizontravel.be",   type: "Company", country: "Belgium",        phone: "+32 2 234 56 78",   totalBookings: 18, city: "Antwerp",     streetName: "Meir",                streetNo: "101", houseNo: "",   boxNo: "5"   },
  { id: 12, name: "Nordic Events AB",        email: "anna.l@nordicevents.se",      type: "Company", country: "Sweden",         phone: "+46 8 123 456 78",  totalBookings: 7,  city: "Stockholm",   streetName: "Kungsgatan",          streetNo: "34",  houseNo: "2",  boxNo: ""    },
  { id: 13, name: "Medici Corporate Travel", email: "g.ferretti@medici.it",        type: "Company", country: "Italy",          phone: "+39 02 8765 4321",  totalBookings: 11, city: "Milan",       streetName: "Via Montenapoleone",   streetNo: "8",   houseNo: "",   boxNo: "14"  },
  { id: 15, name: "Luminary Incentives",     email: "r.holt@luminary.co.uk",       type: "Company", country: "United Kingdom", phone: "+44 20 7123 4567",  totalBookings: 22, city: "London",      streetName: "Canary Wharf",        streetNo: "25",  houseNo: "C",  boxNo: "9"   },
];

export const MOCK_SUPPLIERS = [
  { id: 1, name: "Airtuerk",         type: "API" },
  { id: 2, name: "Hotelbeds",        type: "API" },
  { id: 3, name: "Diana Travel",     type: "API" },
  { id: 4, name: "World2Meet",       type: "API" },
  { id: 5, name: "Manual Supplier",  type: "Manual" },
];

export const MOCK_HOTELS = [
  { id: 1, name: "Rixos Premium Belek", stars: 5, city: "Belek", country: "Turkey", address: "Ileribasi Mevkii, Belek" },
  { id: 2, name: "Rixos Sungate", stars: 5, city: "Kemer", country: "Turkey", address: "Beldibi Mevkii, Kemer" },
  { id: 3, name: "Titanic Mardan Palace", stars: 5, city: "Antalya", country: "Turkey", address: "Kundu, Antalya" },
  { id: 4, name: "Concorde De Luxe Resort", stars: 5, city: "Antalya", country: "Turkey", address: "Lara Yolu, Antalya" },
  { id: 5, name: "Voyage Belek Golf & Spa", stars: 5, city: "Belek", country: "Turkey", address: "Kadriye Mah, Belek" },
  { id: 6, name: "Iberostar Albufera Playa", stars: 4, city: "Mallorca", country: "Spain", address: "Platja de Muro, Mallorca" },
  { id: 7, name: "Hotel Barceló Tenerife", stars: 5, city: "Tenerife", country: "Spain", address: "Puerto de la Cruz, Tenerife" },
  { id: 8, name: "ClubHotel Riu Vistamar", stars: 4, city: "Gran Canaria", country: "Spain", address: "Amadores, Gran Canaria" },
];

let _pid = 1000;
export const generateProductId = () => `PRD-${++_pid}`;

export const newCruiseCabin = () => ({
  id: `cab-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  cabinType: "", cabinNumber: "", boardType: "",
});

export const newCruisePort = () => ({
  id: `port-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  portName: "",
});

export const newAdditionalDriver = () => ({
  id: `drv-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  name: "", age: "",
});

export const Icons = {
  plane: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
    </svg>
  ),
  hotel: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"/><path d="m9 16 .348-.24c1.465-1.013 3.84-1.013 5.304 0L15 16"/><path d="M8 7h.01"/><path d="M16 7h.01"/><path d="M12 7h.01"/><path d="M12 11h.01"/><path d="M16 11h.01"/><path d="M8 11h.01"/><path d="M10 22v-6.5m4 0V22"/>
    </svg>
  ),
  box: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  plus: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="M12 5v14"/>
    </svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
    </svg>
  ),
  search: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
    </svg>
  ),
  chevronDown: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6"/>
    </svg>
  ),
  clipboard: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    </svg>
  ),
  users: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  link: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  ),
  wallet: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2.5"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>
    </svg>
  ),
  fileCheck: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="m9 15 2 2 4-4"/>
    </svg>
  ),
  car: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>
    </svg>
  ),
  shield: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>
    </svg>
  ),
  gift: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>
    </svg>
  ),
  stamp: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 22h14"/><path d="M19.27 13.73A2.5 2.5 0 0 0 17.5 13h-11A2.5 2.5 0 0 0 4 15.5V17a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1.5c0-.66-.26-1.3-.73-1.77Z"/><path d="M14 13V8.5C14 7 15 7 15 5a3 3 0 0 0-6 0c0 2 1 2 1 3.5V13"/>
    </svg>
  ),
  bus: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/>
    </svg>
  ),
  anchor: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="3"/><path d="M12 22V8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/>
    </svg>
  ),
  save: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/>
    </svg>
  ),
  trash: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
    </svg>
  ),
  edit: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>
    </svg>
  ),
  arrowRight: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
    </svg>
  ),
  chevronRight: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 18 6-6-6-6"/>
    </svg>
  ),
  mapPin: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  starFill: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  ),
  user: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  building: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>
    </svg>
  ),
  eye: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  calendar: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/>
    </svg>
  ),
  lock: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  fileText: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>
    </svg>
  ),
  messageSquare: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  dollarSign: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  copy: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
    </svg>
  ),
  listCheck: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 18H3"/><path d="m15 18 2 2 4-4"/><path d="M16 12H3"/><path d="M16 6H3"/>
    </svg>
  ),
};

export const PRODUCT_ICON_MAP = {
  Flight: Icons.plane, Hotel: Icons.hotel, Transfer: Icons.bus,
  Insurance: Icons.shield, Package: Icons.gift, CarRental: Icons.car,
  Visa: Icons.stamp, Cruise: Icons.anchor,
};

export const PRODUCT_COLOR_MAP = {
  Flight:    { bg: "#eef3ff", color: "#3b5bdb", border: "#d0d9f6" },
  Hotel:     { bg: "#fff4e6", color: "#d97706", border: "#fde4b0" },
  Transfer:  { bg: "#e6fcf5", color: "#0d7a4f", border: "#b2f2d9" },
  Insurance: { bg: "#f3f0ff", color: "#6741d9", border: "#d4c8f5" },
  Package:   { bg: "#fff0f6", color: "#c2255c", border: "#f1c0d8" },
  CarRental: { bg: "#e7f5ff", color: "#1971c2", border: "#b8d9f5" },
  Visa:      { bg: "#f8f0fc", color: "#9c36b5", border: "#e0bfed" },
  Cruise:    { bg: "#e3fafc", color: "#0c8599", border: "#99e9f2" },
};

export const STEP_ICON_MAP = [Icons.clipboard, Icons.box, Icons.users, Icons.link, Icons.listCheck, Icons.wallet, Icons.fileCheck];

export const PRODUCT_SHORT_LABELS = {
  Flight: "Flight", Hotel: "Hotel", Transfer: "Transfer",
  Insurance: "Insurance", Package: "Package", CarRental: "Car Rental",
  Visa: "Visa", Cruise: "Cruise",
};

export const COMMERCIAL_ROLES = [
  { key: "MERCHANT", label: "Merchant (own risk)" },
  { key: "AGENT", label: "Agent (intermediary)" },
];
 
// 2. Replace the existing newFlightLeg function with this version:
//
export const newFlightLeg = () => ({
  id: `leg-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  direction: "",
  departure: "", departureName: "", arrival: "", arrivalName: "", date: "", time: "",
  arrivalDate: "", arrivalTime: "",
  airlineCode: "", airlineName: "", flightNumber: "",
  cabinClass: "", fareClass: "", fareClassName: "", fareClassId: null,
  flightStatus: "",
  pnr: "",
  ticketNumber: "",
  supplierRef: "",
  baggageHandbag: false, baggageHandbagKg: 0, baggageHandbagPc: 0,
  baggageCabin: false, baggageCabinKg: 0, baggageCabinPc: 0,
  baggageChecked: false, baggageCheckedKg: 0, baggageCheckedPc: 0,
  baggageCheckedInfant: false, baggageCheckedInfantKg: 0, baggageCheckedInfantPc: 0,
  // RT Full Flight inbound autofill — override flags
  // Set to true when user manually changes the corresponding field; once set,
  // the autofill rule will no longer overwrite it.
  _overDeparture: false, _overArrival: false, _overAirline: false,
});
 